import BreadCrumbs from 'components/breadcrumbs'
import { ArrowLeft } from 'react-feather'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, CardBody, Col, Row } from 'reactstrap'
import { formatDatePMBV2 } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_PROCESSING_REPORT } from 'utils/pathUrl'
import { useFetchDetailProcessingReportsQuery } from './processReportApiSlice'
import { useMemo } from 'react'
import { useGetSchemaDetailQuery } from '../map-schema/mapSchemaApiSlice'

const DetailProcess = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get('id')
  const { data } = useFetchDetailProcessingReportsQuery(
    {
      processUniqueId: id,
    },
    { skip: !id },
  )

  const schemaId = data ? data?.items?.[0]?.processschemaId : null

  const { data: dataSchema } = useGetSchemaDetailQuery(schemaId, {
    skip: !schemaId,
  })

  const { totalRaw, totalOutput, rawConversion, rawUnit, outputUnit } =
    useMemo(() => {
      const rawList = data?.rawMaterials ?? []
      const outputList = data?.items ?? []

      const rawSum = rawList.reduce(
        (sum, item) => sum + (Number(item?.amount) || 0),
        0,
      )
      const outputSum = outputList.reduce(
        (sum, item) => sum + (Number(item?.amount) || 0),
        0,
      )
      const percentage = rawSum > 0 ? (outputSum / rawSum) * 100 : 0

      return {
        totalRaw: rawSum,
        totalOutput: outputSum,
        rawConversion: percentage,
        rawUnit: rawList[0]?.unit ?? '',
        outputUnit: outputList[0]?.unit ?? '',
      }
    }, [data?.items, data?.rawMaterials])

  const {
    schemaInputTotal,
    schemaOutputTotal,
    schemaBenchmarkPercent,
    expectedSchemaRatio,
    actualRatio,
  } = useMemo(() => {
    const inputs = dataSchema?.input ?? []
    const outputs = dataSchema?.output ?? []

    const inputSum = inputs.reduce(
      (sum, item) => sum + (Number(item?.reqAmount) || 0),
      0,
    )
    const outputSum = outputs.reduce(
      (sum, item) => sum + (Number(item?.amount) || 0),
      0,
    )

    const expectedRatio = inputSum > 0 ? outputSum / inputSum : 0
    const actual = totalRaw > 0 ? totalOutput / totalRaw : 0
    const achievement = expectedRatio > 0 ? (actual / expectedRatio) * 100 : 0

    return {
      schemaInputTotal: inputSum,
      schemaOutputTotal: outputSum,
      schemaBenchmarkPercent: achievement,
      expectedSchemaRatio: expectedRatio,
      actualRatio: actual,
    }
  }, [dataSchema?.input, dataSchema?.output, totalOutput, totalRaw])

  const performanceSummary = useMemo(() => {
    if (!totalRaw && !totalOutput)
      return 'Belum ada data material mentah atau barang jadi untuk menghitung performa.'

    const baseExplanation = `Konversi dihitung dari Total Output ÷ Total Barang Mentah × 100% = ${rawConversion.toFixed(2)}%.`
    return `${baseExplanation}`
  }, [rawConversion, totalOutput, totalRaw])

  const schemaBenchmarkExplanation = useMemo(() => {
    if (!schemaInputTotal || !schemaOutputTotal)
      return 'Belum ada data skema untuk menghitung pencapaian terhadap benchmark.'

    const targetText = `${expectedSchemaRatio.toFixed(0)} output per 1 input (skema)`
    const actualText = `${actualRatio.toFixed(2)} output per 1 input (aktual)`
    return `Rasio aktual ${actualText}, target skema ${targetText}. Pencapaian ${schemaBenchmarkPercent.toFixed(2)}%.`
  }, [
    schemaInputTotal,
    schemaOutputTotal,
    expectedSchemaRatio,
    actualRatio,
    schemaBenchmarkPercent,
  ])

  return (
    <>
      <div className="w-100 px-3">
        <div className="d-flex align-items-start gap-2 pt-3">
          <BreadCrumbs
            breadCrumbParent={'Laporan Proses'}
            breadCrumbParentLink={URL_PROCESSING_REPORT}
            breadCrumbActive={'Detail Proses'}
          />
        </div>
        <div className="py-3">
          <Row className="align-items-start mb-3 ">
            <Col
              xs="12"
              md="6"
              className="d-flex gap-1 align-items-center mb-2 mb-md-0"
            >
              <ArrowLeft
                size={20}
                className="me-2"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(-1)}
              />
              <h6 className="fw-bold mb-0">Detail Proses</h6>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="12">
              <Card className="shadow-sm border-0 h-100" data-testid="process-detail-container">
                <CardBody>
                  <h6 className="fw-semibold mb-3">Informasi Proses</h6>
                  <Row className="mb-3">
                    <Col xs="12" sm="2" className="fw-light text-sm">
                      Tanggal
                    </Col>
                    <Col xs="12" sm="10" className="fw-semibold text-sm">
                      {formatDatePMBV2(data?.createdAt) || '-'}
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col xs="12" sm="2" className="fw-light text-sm">
                      Nama Proses
                    </Col>
                    <Col xs="12" sm="10" className="fw-semibold text-sm">
                      {data?.processingName || '-'}
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col xs="12" sm="2" className="fw-light text-sm">
                      Project
                    </Col>
                    <Col xs="12" sm="10" className="fw-semibold text-sm">
                      {data?.projectLabel || '-'}
                    </Col>
                  </Row>
                  <Row className="mb-0">
                    <Col xs="12" sm="2" className="fw-light text-sm">
                      Dibuat oleh
                    </Col>
                    <Col xs="12" sm="10" className="fw-semibold text-sm">
                      {data?.creatorName || '-'}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md="6">
              <Card className="shadow-sm border-0 h-100">
                <CardBody>
                  <h6 className="fw-semibold mb-3">Barang Mentah</h6>
                  {data?.rawMaterials?.length ? (
                    data.rawMaterials.map((item, idx) => (
                      <div
                        key={`${item?.inventoryId ?? idx}-${idx}`}
                        className="mb-3"
                      >
                        <div className="d-flex justify-content-between">
                          <span className="fw-semibold">
                            {item?.nameItem || '-'}
                          </span>
                          <span className="fw-semibold">
                            {`${item?.amount != null ? newFormatThousandSeparator(item?.amount) : '-'} ${item?.unit ?? ''}`.trim()}
                          </span>
                        </div>
                        <small className="text-secondary">
                          Lot ID: {item?.lotCode || '-'}
                        </small>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted mb-0">Tidak ada barang mentah</p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md="6">
              <Card className="shadow-sm border-0 h-100">
                <CardBody>
                  <h6 className="fw-semibold mb-3">Barang Jadi</h6>
                  {data?.items?.length ? (
                    data.items.map((item, idx) => (
                      <div
                        key={`${item?.inventoryId ?? idx}-finished-${idx}`}
                        className="mb-3"
                      >
                        <div className="d-flex justify-content-between">
                          <span className="fw-semibold">
                            {item?.nameItem || '-'}
                          </span>
                          <span className="fw-semibold">
                            {`${item?.amount != null ? newFormatThousandSeparator(item.amount) : '-'} ${item?.unit ?? ''}`.trim()}
                          </span>
                        </div>
                        <small className="text-secondary">
                          Lot ID: {item?.lotCode || '-'}
                        </small>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted mb-0">Tidak ada barang jadi</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className=" mt-3">
            <Col md="12">
              <Card className="shadow-sm border-0 h-100">
                <CardBody>
                  <h6 className="fw-semibold mb-3">Performa</h6>

                  <Row className="g-3">
                    <Col xs="6" md="3">
                      <div
                        className="p-3 rounded text-center h-100"
                        style={{
                          backgroundColor: '#e8f5e9',
                          border: '1px solid #c8e6c9',
                        }}
                      >
                        <p className="text-muted mb-1 small">
                          Total Bahan Mentah
                        </p>
                        <h5 className="fw-bold mb-1 text-success">
                          {newFormatThousandSeparator(totalRaw)}
                        </h5>
                        <p className="text-muted mb-0 small">{rawUnit}</p>
                      </div>
                    </Col>
                    <Col xs="6" md="3">
                      <div
                        className="p-3 rounded text-center h-100"
                        style={{
                          backgroundColor: '#e3f2fd',
                          border: '1px solid #bbdefb',
                        }}
                      >
                        <p className="text-muted mb-1 small">Total Output</p>
                        <h5 className="fw-bold mb-1 text-primary">
                          {newFormatThousandSeparator(totalOutput)}
                        </h5>
                        <p className="text-muted mb-0 small">{outputUnit}</p>
                      </div>
                    </Col>
                    <Col xs="6" md="3">
                      <div
                        className="p-3 rounded text-center h-100"
                        style={{
                          backgroundColor:
                            rawConversion < 100 ? '#fff3e0' : '#e8f5e9',
                          border: `1px solid ${rawConversion < 100 ? '#ffe0b2' : '#c8e6c9'
                            }`,
                        }}
                      >
                        <p className="text-muted mb-1 small">Konversi Aktual</p>
                        <h5
                          className={`fw-bold mb-1 ${rawConversion < 100
                            ? 'text-warning'
                            : 'text-success'
                            }`}
                        >
                          {rawConversion.toFixed(2)}%
                        </h5>
                        <p className="text-muted mb-0 small">
                          Total Output ÷ Total Bahan Mentah × 100%
                        </p>
                      </div>
                    </Col>
                    <Col xs="6" md="3">
                      <div
                        className="p-3 rounded text-center h-100"
                        style={{
                          backgroundColor:
                            schemaBenchmarkPercent < 100
                              ? '#fce4ec'
                              : '#e8f5e9',
                          border: `1px solid ${schemaBenchmarkPercent < 100 ? '#f8bbd0' : '#c8e6c9'
                            }`,
                        }}
                      >
                        <p className="text-muted mb-1 small">
                          Pencapaian vs Skema
                        </p>
                        <h5
                          className={`fw-bold mb-1 ${schemaBenchmarkPercent < 100
                            ? 'text-danger'
                            : 'text-success'
                            }`}
                        >
                          {schemaBenchmarkPercent.toFixed(2)}%
                        </h5>
                        <p className="text-muted mb-0 small">
                          Rasio aktual ÷ rasio skema × 100%
                        </p>
                      </div>
                    </Col>
                  </Row>
                  <p className="text-muted small mb-0 mt-3">
                    {schemaBenchmarkExplanation}
                  </p>
                  <p className="text-muted small mb-0 mt-1">
                    {performanceSummary}
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  )
}

export default DetailProcess
